import { NUMERIC_COLUMNS } from '../../Constants/globalColConfig';
import { parseCharacteristics, preprocessFilters } from "../OrderComponents/columnConfigUtils";
import { withUiFilterKeyAliases } from "../../Utils/filterKeyAliases";
import { useSelectedLadderOrdersStore } from '../../Store/selectedLadderOrdersStore';
import { fetchBondLadderOrders } from '../../http/api'; // <-- import your API call
import { utcToLocal } from '../../Utils/timeConversions';

// Formatting Functions
export const formatNumber = (value, columnKey, rowData) => {
  if (value === undefined || value === null || value === "") {
    if (columnKey === "orderExpiryDtTime") return "N/A";
    return "---";
  }

  const formatDateToDDMonYY = (dateStr, showTime = true) => {
    const localDt = utcToLocal(dateStr);
    if (!localDt) return "---";
    return showTime ? localDt.format("DMMMYY HH:mm") : localDt.format("DMMMYY");
  };

  if (columnKey === "orderExpiryDtTime") {
    if (rowData && (rowData.protocol === "CLOB" || !rowData.protocol)) {
      return "N/A";
    }
    try {
      let valueStr = value.toString();
      if (!valueStr.endsWith("Z")) {
        valueStr += "Z";
      }
      let date = new Date(valueStr);
      if (!isNaN(date.getTime())) {
        const timeString = date.toISOString().split("T")[1].substring(0, 8);
        return timeString;
      }
      return "---";
    } catch (e) {
      console.error("Error formatting expiry time:", e, "for value:", value);
      return "---";
    }
  }

  if (columnKey === "insured" || columnKey === "altMinTax") {
    return value === 1 ? "Yes" : value === 0 ? "No" : "---";
  }

  if (NUMERIC_COLUMNS.has(columnKey)) {
    if (typeof value === "number" || !isNaN(Number(value))) {
      const num = Number(value);
      const integerColumns = ["qty", "quantity", "minQty", "maxQty", "numberOfRungs"];
      const decimalPlaces = integerColumns.includes(columnKey) ? 0 : 2;
      return num.toLocaleString("en-US", {
        minimumFractionDigits: decimalPlaces,
        maximumFractionDigits: decimalPlaces,
      });
    }
    return "---";
  }

  const dateColumns = ["maturity", "nextCallDate", "createdAt", "dateTime", "maturityDate", "callEndDate", "callStartDate"];
  if (dateColumns.includes(columnKey) && value) {
    if (columnKey === "maturity" || columnKey === "nextCallDate" || columnKey === "maturityDate" || columnKey === "callEndDate" || columnKey === "callStartDate") {
      return formatDateToDDMonYY(value, false);
    }
    return formatDateToDDMonYY(value);
  }

  if (columnKey === "coupon") {
    return value !== null && value !== undefined ? value : "---";
  }

  return value;
};

export const isNumericOrDate = (value, columnKey) => {
  const dateColumns = ["maturity", "nextCallDate", "createdAt", "dateTime", "maturityDate", "callEndDate", "callStartDate"];
  return NUMERIC_COLUMNS.has(columnKey) || dateColumns.includes(columnKey);
};

export const isBuySellColumn = (columnKey) => {
  return columnKey === "side" || columnKey === "action";
};

export const isTypeLikeColumn = (columnKey) => {
  return columnKey === "type" || columnKey === "bondType" || columnKey === "bondSubType";
};

export const isBoldColumn = (columnKey) => {
  return ["securityMarketValue", "totalGL", "realizedGL", "unrealizedGL"].includes(columnKey);
};

export const isGreenColumn = (columnKey) => {
  return ["totalGL", "realizedGL", "unrealizedGL"].includes(columnKey);
};

// Helper to parse ladderCharacteristics and get chips
export const getLadderChips = (ladderCharacteristics) => {
  if (!ladderCharacteristics) return [];
  let parsed;
  try {
    parsed = JSON.parse(ladderCharacteristics);
  } catch {
    return [];
  }

  const allChips = [];

  // Add ladder metadata chips (totalAmount, amountPerRung, etc.)
  const metadataFields = {
    totalAmount: 'Total Amount',
    amountPerRung: 'Amount Per Rung',
    numberOfRungs: 'Rungs',
    totalMaturity: 'Total Maturity',
    // nameOfTheLadder: 'Ladder Name'
  };

  Object.entries(metadataFields).forEach(([key, label]) => {
    if (parsed[key] !== null && parsed[key] !== undefined && parsed[key] !== '') {
      allChips.push({ key, value: parsed[key], isMetadata: true, label });
    }
  });

  // Add characteristics chips if they exist
  if (parsed.characteristics) {
    const raw = parseCharacteristics(parsed.characteristics);
    const processed = preprocessFilters(raw);
    Object.entries(processed).forEach(([key, value]) => {
      allChips.push({ key, value, isMetadata: false });
    });
  }

  return allChips;
};

/**
 * True when a ladder group's orders came from a characteristic that is associated with the
 * ladder — i.e. both ids are present. Such a group is driven by the characteristic, so the
 * blotter does not offer a top-level Edit for it.
 *
 * Checks the child orders as well as the group row: the REST fetch builds a group by
 * spreading a real parent order (so the id is on the row), while the websocket path
 * synthesizes the group from a single incoming order and copies only a few fields.
 */
export const isLadderLinkedCharacteristicGroup = (rowData) => {
  if (!rowData?.bondLadderId) return false;
  if (rowData.characteristicId) return true;
  const orders = rowData.orders || rowData.ladderOrders || [];
  return orders.some((order) => order?.characteristicId);
};

// Utility to process ladder group edit data
export const getProcessedLadderEditData = (rowData) => {
  let ladderChar = {};
  if (rowData.ladderCharacteristics) {
    try {
      ladderChar = JSON.parse(rowData.ladderCharacteristics);
      if (
        ladderChar.characteristics &&
        typeof ladderChar.characteristics === "string"
      ) {
        // Spread into the edit form below, which is keyed by the UI spelling
        // (couponMin, ...) while the stored JSON carries the backend spelling
        ladderChar.characteristics = withUiFilterKeyAliases(
          JSON.parse(ladderChar.characteristics)
        );
      }
    } catch (e) {
      ladderChar = {};
    }
  }
  const processedData = {
    ...ladderChar,
    ...(ladderChar.characteristics || {}),
    ladderName: rowData.nameOfTheLadder || ladderChar.nameOfTheLadder,
    userName: rowData.userName || (rowData.ladderOrders?.length > 0 ? rowData.ladderOrders[0].userName : undefined),
    // pmId: rowData.pmId || (rowData.ladderOrders?.length > 0 ? rowData.ladderOrders[0].pmId : undefined),
    userId: rowData.userId || (rowData.ladderOrders?.length > 0 ? rowData.ladderOrders[0].userId : undefined),
    account: rowData.account || (rowData.ladderOrders?.length > 0 ? rowData.ladderOrders[0].account : undefined),
    side: rowData.side || rowData.action || (rowData.ladderOrders?.length > 0 ? rowData.ladderOrders[0].side : undefined),
    bondLadderId: rowData.bondLadderId,
    fromStagingEdit: true,
    // Optionally add more fields as needed
  };
  processedData.type = processedData.bondType;
  delete processedData.characteristics;
  return processedData;
};

/**
 * Prefill selected ladder orders from backend API response.
 * This is ONLY called when editing from staging blotter.
 * It sets both selectedOrders and originalSelectedOrders.
 * @param {string|number} bondLadderId
 * @param {string} token
 * @returns {Promise<void>}
 */
export const prefillSelectedLadderOrders = async (bondLadderId, token) => {
  if (!bondLadderId || !token) return;
  try {
    const response = await fetchBondLadderOrders(token, bondLadderId);
    const orders = response || [];
    const store = useSelectedLadderOrdersStore.getState();

    // Clear existing orders first
    store.clearAllOrders();

    // Map rungId to order
    const ordersObj = {};
    orders.forEach(order => {
      if (order.bondLadderRungId != null && order.bondInventoryId != null) {
        const rungIndex = order.bondLadderRungId - 1; // rungId is 1-based, store uses 0-based
        ordersObj[rungIndex] = {
          ...order,
          qty: order.quantity,
          quantity: order.quantity,
          // Non-editable if DONE or RFQ
          isEditable: !((order.status === "DONE") || (order.protocol === "RFQ")),
          action: order.side || order.action,
        };
      }
    });

    // Set both selectedOrders and originalSelectedOrders ONLY in this edit flow
    store.setOriginalSelectedOrders(ordersObj);
  } catch (err) {
    // Optionally handle error
    console.error('Failed to prefill ladder orders:', err);
  }
};
